/*!
 * Copyright © 2019. Verizon Connect Ireland Limited. All rights reserved.
 */

import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { isElementNull } from '../../../../utils';
import { RoadsideAssistanceMarketingModalComponent, RoadsideAssistanceModalComponent } from '../../../maps/components';
import { delayedRetry } from '../../operators';
import { ModalWindowService, RoadsideAssistanceHttpService } from '../../services';
import {
  ERoadsideAssistanceActions,
  GetUserRoadsideAssistanceStatus,
  GetUserRoadsideAssistanceStatusSuccess,
  OpenLearnMoreWindow,
  OpenRoadsideAssistanceModal,
  OpenRoadsideAssistanceModalSuccess
} from '../actions';
import { getMapContextMenuPermissions } from '../selectors';

@Injectable()
export class RoadsideAssistanceEffects {
  openRoadsideAssistanceModal$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<OpenRoadsideAssistanceModal>(ERoadsideAssistanceActions.OpenRoadsideAssistanceModal),
      map((action: OpenRoadsideAssistanceModal) => action.payload),
      map(vehicleId => vehicleId.toString()),
      switchMap(vehicleId =>
        this._roadsideAssistanceHttpService.getVehicleDetails(vehicleId).pipe(
          delayedRetry(),
          catchError(() => of(null).pipe(filter(() => false))),
          map(vehicle => {
            const vin = vehicle !== null && vehicle.isActivated ? vehicle.vin : null;
            return new OpenRoadsideAssistanceModalSuccess(vin);
          })
        )
      )
    )
  );

  openRoadsideAssistanceModalSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType<OpenRoadsideAssistanceModalSuccess>(ERoadsideAssistanceActions.OpenRoadsideAssistanceModalSuccess),
        map(action => action.payload),
        withLatestFrom(this._store.pipe(select(getMapContextMenuPermissions))),
        tap(([, permissions]) => {
          const isRsaMarketingLinkEnabled = permissions ? permissions.roadSideAssistanceMarketingLinkEnabled : false;
          if (isRsaMarketingLinkEnabled) {
            this._dialog.open(RoadsideAssistanceMarketingModalComponent);
          } else {
            this._dialog.open(RoadsideAssistanceModalComponent);
          }
        })
      ),
    { dispatch: false }
  );

  getUserRoadsideAssistanceStatus$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetUserRoadsideAssistanceStatus>(ERoadsideAssistanceActions.GetUserRoadsideAssistanceStatus),
      filter(() => isPlatformBrowser(this._platformId)),
      switchMap(() =>
        this._roadsideAssistanceHttpService.getUserRoadsideAssistanceStatus().pipe(
          delayedRetry(),
          catchError(() => of(null))
        )
      ),
      filter(status => !isElementNull(status)),
      map(userRoadsideAssistanceStatus => new GetUserRoadsideAssistanceStatusSuccess(userRoadsideAssistanceStatus))
    )
  );

  openLearnMoreWindow$: Observable<Action> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<OpenLearnMoreWindow>(ERoadsideAssistanceActions.OpenLearnMoreWindow),
        tap(() => this._modalWindowService.openNewTab('https://www.verizonconnect.com/solutions/commercial-roadside-assistance/', '_blank'))
      ),
    { dispatch: false }
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _roadsideAssistanceHttpService: RoadsideAssistanceHttpService,
    private readonly _modalWindowService: ModalWindowService,
    private readonly _dialog: MatLegacyDialog,
    private readonly _store: Store,
    @Inject(PLATFORM_ID) private readonly _platformId: object
  ) {}
}
